import React, { forwardRef } from "react";
import { Check } from "lucide-react";
import { Box } from "@/shared/ui/base";
import { cn } from "@/shared/lib/utils/cn";
import type { BaseComponentProps } from "@/shared/ui/base";

export interface CheckboxProps
  extends
    BaseComponentProps,
    Omit<React.InputHTMLAttributes<HTMLInputElement>, "className" | "type"> {
  label?: React.ReactNode;
  error?: string;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      label,
      error,
      disabled,
      checked,
      className,
      "data-testid": dataTestId,
      ...props
    },
    ref,
  ) => {
    return (
      <Box className={className} data-testid={dataTestId}>
        <Box
          as="label"
          className={cn(
            "flex items-center gap-2",
            disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
          )}
        >
          <Box className="relative">
            <input
              ref={ref}
              type="checkbox"
              checked={checked}
              disabled={disabled}
              className="sr-only peer"
              {...props}
            />
            <Box
              className={cn(
                "w-4 h-4 border rounded transition-colors peer-checked:bg-red-600 peer-checked:border-red-600",
                error ? "border-red-500" : "border-gray-300",
                disabled ? "bg-gray-100" : "bg-white",
              )}
            >
              {checked && (
                <Check className="w-4 h-4 text-white absolute inset-0" />
              )}
            </Box>
          </Box>
          {label && (
            <Box as="span" className="text-sm text-gray-700">
              {label}
            </Box>
          )}
        </Box>

        {error && (
          <Box as="p" className="mt-1 text-sm text-red-500">
            {error}
          </Box>
        )}
      </Box>
    );
  },
);

Checkbox.displayName = "Checkbox";
